import { isEmpty, stringUtils } from '@festina-life/core'
import cx from 'classnames'
import { type ReactNode, useState } from 'react'

import { Tooltip } from '../../feedback/tooltip/Tooltip'
import { paperStyles } from '../../libs/paperStyles'
import { reactNodeToString } from '../../libs/reactNodeToString'
import { Icon } from '../icon/Icon'
import { Typography } from '../typography/Typography'

type Props = {
  title: ReactNode
  value?: ReactNode
  secondaryValue?: ReactNode
  tooltip?: ReactNode
  isHiddenWithNoData?: boolean
}

export function CardItem({
  title,
  value,
  secondaryValue,
  tooltip,
  isHiddenWithNoData,
}: Props) {
  const [isHovering, setIsHovering] = useState(false) // We can't use css hover since intensity is a prop
  const hasEmptyValue = isEmpty(value) || value === '-' || value === ''

  if (isHiddenWithNoData && hasEmptyValue) {
    return null
  }

  const Title = () => (
    <Typography intensity={isHovering ? 'primary' : 'secondary'}>
      {title}
    </Typography>
  )

  return (
    <div
      className={cx(
        'flex items-end gap-1 group/carditem', // named group to prevent funky behavior when nested inside another group
        '-my-1 py-1 -mx-1.5 px-1.5', // styles to increase hover target
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {!tooltip ? (
        <Title />
      ) : (
        <Tooltip tip={tooltip} className="inline-flex!">
          <Title />
          <Icon name="status.info" color="unset" className="text-info ms-1" />
        </Tooltip>
      )}
      <div
        className={cx(
          'grow self-end mb-0.5 min-w-2',
          paperStyles({ borders: ['bottom'] }),
          'group-hover/carditem:border-b-slate-400 dark:group-hover/carditem:border-b-slate-600 transition-colors',
        )}
      />
      <div
        className="contents"
        data-testid={`card-item-value-${reactNodeToString(title)}`}
      >
        {secondaryValue && (
          <Typography className="wrap-anywhere text-right" intensity="tertiary">
            {secondaryValue}
          </Typography>
        )}
        <Typography className="wrap-anywhere text-right">
          {stringUtils.makePaddingSpacesWider(value)}
        </Typography>
      </div>
    </div>
  )
}
